import {
  Component,
  createMemo,
  createSignal,
  For,
  onMount,
  Show,
} from "solid-js";
import { api } from "../../api";
import { Spinner } from "../ui";

interface RepoItem {
  name: string;
  full_name: string;
  description: string | null;
  language: string | null;
  private: boolean;
}

interface AccessibleReposResponse {
  repos: RepoItem[];
}

export interface RepoPickerProps {
  projectId: string;
  onSelect: (owner: string, repo: string, repoItem: RepoItem) => void;
  onFallback?: () => void;
  // When provided, surface an affordance to install/configure the GitHub App so
  // users can reach a repo that isn't covered by an existing installation
  // instead of dead-ending at manual entry (FLOW-494).
  onInstall?: () => void;
}

export const RepoPicker: Component<RepoPickerProps> = (props) => {
  const [repos, setRepos] = createSignal<RepoItem[]>([]);
  const [loading, setLoading] = createSignal(true);
  const [empty, setEmpty] = createSignal(false);
  const [search, setSearch] = createSignal("");

  const filtered = createMemo(() => {
    const q = search().toLowerCase();
    if (!q) return repos();
    return repos().filter(
      (r) =>
        r.full_name.toLowerCase().includes(q) ||
        (r.description?.toLowerCase().includes(q) ?? false),
    );
  });

  onMount(async () => {
    try {
      const data = await api.get<AccessibleReposResponse>(
        "/github/repos/accessible",
      );
      if (!data.repos || data.repos.length === 0) {
        // GitHub not configured or no accessible repos — stay visible so the
        // user can see an explanation and consciously choose to enter manually.
        setEmpty(true);
      } else {
        setRepos(data.repos);
      }
    } catch {
      // Hard network/API error — fall back immediately, nothing useful to show.
      props.onFallback?.();
    } finally {
      setLoading(false);
    }
  });

  const handleSelect = (repo: RepoItem) => {
    const [owner, name] = repo.full_name.split("/");
    props.onSelect(owner, name, repo);
  };

  return (
    <Show
      when={!loading()}
      fallback={
        <div class="flex justify-center py-8">
          <Spinner size="md" />
        </div>
      }
    >
      {/* Empty state: GitHub unconfigured or no accessible repos */}
      <Show when={empty()}>
        <div class="rounded border border-line px-4 py-5 text-center space-y-3">
          <p class="text-sm text-ink-2">
            No accessible repositories found. GitHub may not be configured on
            this instance, or the App isn't installed on the repository you
            want.
          </p>
          <Show when={props.onInstall}>
            <button
              type="button"
              class="text-sm font-medium text-ink-2 hover:text-ink hover:underline"
              onClick={() => props.onInstall?.()}
            >
              Install the GitHub App on a repository →
            </button>
          </Show>
          <button
            type="button"
            class="block w-full text-sm text-ink-3 hover:text-ink hover:underline"
            onClick={() => props.onFallback?.()}
          >
            Enter owner / repository manually →
          </button>
        </div>
      </Show>

      {/* Picker: repos loaded */}
      <Show when={!empty()}>
        <div class="space-y-3">
          <input
            type="text"
            placeholder="Search repositories..."
            value={search()}
            onInput={(e) => setSearch(e.currentTarget.value)}
            class="w-full px-3 py-2 border border-line rounded text-sm focus-ring"
          />
          <div class="max-h-72 overflow-y-auto rounded border border-line divide-y divide-line">
            <Show
              when={filtered().length > 0}
              fallback={
                <p class="text-sm text-ink-3 text-center py-6">
                  No repositories match your search.
                </p>
              }
            >
              <For each={filtered()}>
                {(repo) => (
                  <button
                    type="button"
                    class="w-full text-left px-4 py-3 hover:bg-sunken transition-colors"
                    onClick={() => handleSelect(repo)}
                  >
                    <div class="flex items-center justify-between gap-2">
                      <span class="font-medium text-ink text-sm truncate">
                        {repo.full_name}
                      </span>
                      <span
                        class={`flex-shrink-0 text-xs px-1.5 py-0.5 rounded font-medium ${
                          repo.private
                            ? "bg-surface text-ink-2 border border-line"
                            : "bg-signal-green-tint text-signal-green"
                        }`}
                      >
                        {repo.private ? "Private" : "Public"}
                      </span>
                    </div>
                    <Show when={repo.description}>
                      <p class="text-xs text-ink-3 mt-0.5 truncate">
                        {repo.description}
                      </p>
                    </Show>
                    <Show when={repo.language}>
                      <p class="text-xs text-ink-3 mt-0.5">{repo.language}</p>
                    </Show>
                  </button>
                )}
              </For>
            </Show>
          </div>
          <div class="flex items-center justify-between gap-2">
            <Show when={props.onInstall}>
              <button
                type="button"
                class="text-xs font-medium text-ink-2 hover:text-ink hover:underline"
                onClick={() => props.onInstall?.()}
              >
                Don't see your repository? Install or configure the GitHub App →
              </button>
            </Show>
            <button
              type="button"
              class="text-xs text-ink-3 hover:text-ink hover:underline"
              onClick={() => props.onFallback?.()}
            >
              Enter owner / repository manually instead →
            </button>
          </div>
        </div>
      </Show>
    </Show>
  );
};
